import { useEffect, useState, useRef } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import { io } from "socket.io-client";
import L from "leaflet";
import api from "../services/api";
import { BASE_URL } from "../services/api";

// ===== ICÔNE TAXI =====
const taxiIcon = L.divIcon({
  className: "",
  html: `<div style="
    background:#0B3C5D; color:#FFA500;
    border-radius:50%; width:36px; height:36px;
    display:flex; align-items:center; justify-content:center;
    font-size:18px; box-shadow:0 2px 8px rgba(0,0,0,0.3);
    border:2px solid #FFA500;
  ">🚗</div>`,
  iconSize:   [36, 36],
  iconAnchor: [18, 18],
});

// ===== AUTO RECENTER =====
function AutoCenter({ position }) {
  const map = useMap();
  useEffect(() => {
    if (position) map.panTo(position, { animate: true });
  }, [position, map]);
  return null;
}

const socket = io(BASE_URL, {
  auth: { token: localStorage.getItem("adminToken") },
});

export default function TrackingPage() {
  const [drivers,     setDrivers]     = useState({});
  const [activeRides, setActiveRides] = useState([]);
  const [selected,    setSelected]    = useState(null);
  const [connected,   setConnected]   = useState(false);

  // ===== SOCKET =====
  useEffect(() => {
    socket.on("connect",    () => setConnected(true));
    socket.on("disconnect", () => setConnected(false));

    // Position chauffeur en temps réel
    socket.on("driver_location_update", (data) => {
      if (!data.ride_id) return;
      setDrivers(prev => ({
        ...prev,
        [data.ride_id]: {
          position: [
            parseFloat(data.lat ?? data.latitude),
            parseFloat(data.lng ?? data.longitude),
          ],
          rideId: data.ride_id,
          trail: [...(prev[data.ride_id]?.trail ?? []), [
            parseFloat(data.lat ?? data.latitude),
            parseFloat(data.lng ?? data.longitude),
          ]].slice(-50), // garde les 50 derniers points
        },
      }));
    });

    return () => {
      socket.off("connect");
      socket.off("disconnect");
      socket.off("driver_location_update");
    };
  }, []);

  // ===== COURSES ACTIVES =====
  useEffect(() => {
    api.get("/admin/rides?status=started")
      .then(res => setActiveRides(res.data))
      .catch(console.error);
  }, []);

  const selectedDriver = selected ? drivers[selected] : null;

  return (
    <div style={{ padding: "1.5rem 0" }}>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1.5rem" }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 500, color: "#0B3C5D" }}>📍 Tracking Live</h1>
          <p style={{ fontSize: 14, color: "#888", marginTop: 4 }}>Suivi en temps réel des courses</p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{
            display: "flex", alignItems: "center", gap: 6,
            fontSize: 12, padding: "4px 14px", borderRadius: 20,
            background: connected ? "#EAF3DE" : "#FCEBEB",
            color: connected ? "#3B6D11" : "#A32D2D",
            border: `0.5px solid ${connected ? "#B5D4A0" : "#F5C6C6"}`,
          }}>
            <div style={{ width: 7, height: 7, borderRadius: "50%", background: connected ? "#3B6D11" : "#A32D2D" }} />
            {connected ? "Connecté" : "Déconnecté"}
          </div>
          <div style={{ fontSize: 12, padding: "4px 14px", borderRadius: 20, background: "#E6F1FB", color: "#185FA5", border: "0.5px solid #B5D4F4" }}>
            🚗 {Object.keys(drivers).length} en course
          </div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 280px", gap: 16 }}>

        {/* Carte */}
        <div style={{ borderRadius: 12, overflow: "hidden", border: "0.5px solid #e5e5e5" }}>
          <MapContainer
            center={[16.0179, -16.4896]}
            zoom={14}
            style={{ height: "75vh", width: "100%" }}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution="© OpenStreetMap"
            />

            {selectedDriver && (
              <AutoCenter position={selectedDriver.position} />
            )}

            {Object.values(drivers).map(d => (
              <div key={d.rideId}>
                <Marker
                  position={d.position}
                  icon={taxiIcon}
                  eventHandlers={{ click: () => setSelected(d.rideId) }}
                >
                  <Popup>
                    <div style={{ fontSize: 13 }}>
                      <strong style={{ color: "#0B3C5D" }}>Course #{d.rideId}</strong><br/>
                      📍 {d.position[0].toFixed(4)}, {d.position[1].toFixed(4)}
                    </div>
                  </Popup>
                </Marker>

                {/* Trajet */}
                {d.trail.length > 1 && (
                  <Polyline
                    positions={d.trail}
                    color="#0B3C5D"
                    weight={3}
                    opacity={0.6}
                    dashArray="6 4"
                  />
                )}
              </div>
            ))}
          </MapContainer>
        </div>

        {/* Panel courses actives */}
        <div style={{ background: "#fff", borderRadius: 12, border: "0.5px solid #e5e5e5", padding: 16, overflowY: "auto", maxHeight: "75vh" }}>
          <p style={{ fontSize: 14, fontWeight: 600, color: "#0B3C5D", marginBottom: 12 }}>
            Courses en cours ({activeRides.length})
          </p>

          {activeRides.length === 0 && (
            <p style={{ fontSize: 13, color: "#aaa", textAlign: "center", marginTop: 40 }}>
              Aucune course active
            </p>
          )}

          {activeRides.map(r => (
            <div
              key={r.id}
              onClick={() => setSelected(r.id)}
              style={{
                padding: "10px 12px",
                borderRadius: 8,
                marginBottom: 8,
                cursor: "pointer",
                border: `0.5px solid ${selected === r.id ? "#0B3C5D" : "#e5e5e5"}`,
                background: selected === r.id ? "#E6F1FB" : "#f7f7f7",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: "#0B3C5D" }}>
                  Course #{r.id}
                </span>
                <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, background: "#EAF3DE", color: "#3B6D11" }}>
                  En cours
                </span>
              </div>
              <div style={{ fontSize: 12, color: "#888" }}>👤 {r.client ?? "—"}</div>
              <div style={{ fontSize: 12, color: "#888" }}>🚗 {r.driver ?? "—"}</div>
              <div style={{ fontSize: 12, fontWeight: 500, color: "#FFA500", marginTop: 4 }}>
                {Number(r.prix ?? 0).toLocaleString("fr-FR")} CFA
              </div>
              {drivers[r.id] && (
                <div style={{ fontSize: 11, color: "#3B6D11", marginTop: 4 }}>
                  📡 Position reçue
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}